<template>
    <div class="p-6 pb-0">
        <BreadCrumbs :navigation="navigation" />
        <Loader :visible="isLoading" :text="loadingText" />

        <div class="mt-6">
            <div class="flex justify-end mb-4">
                <input 
                    type="file" 
                    ref="csvInput" 
                    class="hidden" 
                    accept=".csv" 
                    @change="handleCsvFile" 
                />
                
                <PrimaryButton 
                    class="bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500"
                    @click="triggerImport"
                    v-if="csvImportEnabled"
                >
                    <i class="fa fa-file-csv mr-2"></i> {{ $t("actions.import_csv") }}
                </PrimaryButton>
            </div>

            <div v-if="csvProcessedRows.length === 0" class="mb-6">
                <label class="block text-sm font-medium mb-2">
                    {{ $t("gm.warehouse_choice") }}
                </label>
                <v-select
                    v-model="selectedStorage"
                    :options="storages"
                    :reduce="s => s.Storage"
                    label="Storage"
                    :disabled="isDefaultStorageConfigured"
                    :placeholder="$t('sc.select_storage_placeholder')"
                    :class="{
                        'bg-gray-100 cursor-not-allowed': isDefaultStorageConfigured,
                        'bg-white': !isDefaultStorageConfigured
                    }"
                >
                    <template #option="{ Storage, Description }">
                        {{ Storage }} ( {{ Description }} )
                    </template>
                    <template #selected-option="{ Storage, Description }">
                        {{ Storage }} ( {{ Description }} )
                    </template>
                </v-select>
                <v-select
                    v-if="selectedStorage && !scaricoManualeMultiplo"
                    v-model="selectedReason"
                    :options="filteredReasons"
                    :reduce="r => r.Reason"
                    label="Reason"
                    :placeholder="$t('sc.select_reason_placeholder')"
                    :class="{
                        'bg-gray-100 cursor-not-allowed mt-2': !selectedStorage,
                        'bg-white mt-2': selectedStorage
                    }"
                    :disabled="!selectedStorage || scaricoManualeMultiplo"
                >
                    <template #option="{ Reason, Description }">
                        {{ Reason }} - {{ Description }}
                    </template>
                    <template #selected-option="{ Reason, Description }">
                        {{ Reason }} - {{ Description }}
                    </template>
                </v-select>
                <p
                    v-if="selectedStorage && scaricoManualeMultiplo"
                    class="text-s text-gray-500 italic mt-1"
                >
                    <i class="fa fa-info-circle mr-1"></i>
                    {{ $t('sc.reason_after_first_item_info') }}
                </p>

            </div>

            <div v-if="selectedStorage && csvProcessedRows.length === 0 && (scaricoManualeMultiplo || selectedReason)" class="mb-6">
                <div class="flex items-center justify-between mb-4">
                    <label class="block text-sm font-medium mb-2">
                        {{ $t("core.choose_item") }}
                    </label>
                </div>

                <div class="flex gap-4 mb-4" role="radiogroup">
                    <label class="flex items-center gap-2">
                        <input v-model="searchMode" type="radio" value="barcode" name="itemSearchMode" class="text-blue-600 focus:ring-blue-500" />
                        <span>{{ $t("sc.item") }}</span>
                    </label>
                    <label class="flex items-center gap-2">
                        <input v-model="searchMode" type="radio" value="lot" name="itemSearchMode" class="text-blue-600 focus:ring-blue-500" />
                        <span>{{ $t("sc.lot") }}</span>
                    </label>
                </div>

                <div class="mb-6">
                    <div class="relative">
                        <input
                            ref="barcodeInput"
                            v-model="barcodeSearch"
                            type="text"
                            :placeholder="(searchMode === 'lot' ? $t('sc.lot_search') : $t('sc.item_or_barcode')) + '...'"
                            class="w-full p-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            @keyup.enter="searchSelectedItem"
                            @keydown.enter.prevent
                            autofocus
                        />
                        <div class="absolute right-3 top-3 text-gray-400">
                            <i class="fa fa-search"></i>
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmationModal :show="showProductSelectionModal" @close="showProductSelectionModal = false">
                <template #title>{{ $t("sc.select_product") }}</template>
                <template #content>
                    <div class="text-sm text-gray-600 mb-4">{{ $t("sc.multiple_products_found") }}</div>
                    <div class="max-h-96 overflow-y-auto space-y-2">
                        <div v-for="(product, idx) in productOptions" :key="idx"
                            class="p-3 border rounded hover:bg-blue-50 cursor-pointer flex justify-between items-center group"
                            @click="selectProductForDischarge(product); showProductSelectionModal = false;">
                            <div>
                                <div class="font-bold">{{ product.Item }}</div>
                                <div class="text-sm text-gray-600">{{ product.Description }}</div>
                                <div class="flex flex-wrap gap-2 mt-1">
                                    <span v-if="product.Lot" class="text-xs font-mono bg-yellow-50 px-1 border border-yellow-200 rounded">LOT: {{ product.Lot }}</span>
                                    <span class="text-xs font-mono bg-gray-50 px-1 border border-gray-200 rounded">DEP: {{ product.Storage }}</span>
                                    <div v-if="product.SpecificatorType" class="flex flex-col">
                                        <span class="text-[10px] text-gray-500 uppercase">{{ product.SpecificatorType == MagoEnums.Specificator_Type.Customer ? $t('sc.specificator_customer') : (product.SpecificatorType == MagoEnums.Specificator_Type.Supplier ? $t('sc.specificator_supplier') : '') }}</span>
                                        <span class="text-[10px] font-semibold text-blue-900 truncate max-w-[150px]">{{ product.SpecificatorName }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="text-right">
                                <div class="text-sm font-semibold">{{ $t('sc.stock_short') }} {{ product.UoM }} {{ Number(product.BookInv) }}</div>
                            </div>
                        </div>
                    </div>
                </template>
                <template #footer>
                    <DangerButton @click="showProductSelectionModal = false">{{ $t("actions.cancel") }}</DangerButton>
                </template>
            </ConfirmationModal>

            <ConfirmationModal :show="showDuplicateModal" @close="showDuplicateModal = false">
                <template #title>{{ $t('sc.duplicate_item_title') }}</template>
                <template #content>
                    <div class="text-sm text-gray-600 mb-4">
                        {{ $t('sc.duplicate_item_message') }}
                    </div>
                </template>
                <template #footer>
                    <DangerButton @click="showDuplicateModal = false">{{ $t('actions.cancel') }}</DangerButton>
                    <PrimaryButton class="ms-3" @click="handleConfirmDuplicate" >{{ $t('actions.ok') }}</PrimaryButton>
                </template>
            </ConfirmationModal>


            <ConfirmationModal :show="showDischargeModal" @close="showDischargeModal = false">
                <template #title>{{ $t("sc.choose_quantity") }}</template>
                <template #content>
                    <div v-if="currentProduct">
                        <div class="flex flex-col gap-2 mb-4 text-start " >
                            <p><strong>{{ $t("sc.article") }}</strong> {{ currentProduct.Item }}</p>
                            <template v-if="!availableLots.length">
                                <p><strong>{{ $t("sc.lot") }}:</strong> {{ currentProduct.Lot }}</p>
                            </template>
                            <p><strong>{{ $t("sc.storage") }}</strong> {{ currentProduct.Storage }}</p>
                            <p><strong>{{ $t("sc.description") }}</strong> {{ currentProduct.Description }}</p>
                            <p><strong>{{ $t("sc.stock") }} </strong> {{ currentProduct.UoM }} {{ Number(currentProduct.BookInv) || 0 }}  </p>
                            <p><strong>{{ $t("sc.minimum_stock") }}</strong> {{ currentProduct.UoM }} {{ Number(currentProduct.MinimumStock) || 0 }}  </p>
                            
                        </div>
                        <div v-if="hasLotSelection" class="mb-4">
                            <label class="block text-sm font-medium mb-2">{{ $t("sc.lot") }}</label>
                            <select v-model="selectedLot" class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                                <option v-for="lot in availableLots" :key="lot" :value="lot">
                                    {{ lot }} - {{ lotItemsByLot[lot]?.UoM }} {{ lotItemsByLot[lot]?.BookInv }} 
                                </option>
                            </select>
                        </div>
                        <div class="mb-4">
                            <label class="block text-sm font-medium mb-2">{{ $t("sc.quantity_picked") }}</label>
                            <input ref="quantityInput" v-model.number="dischargeQuantity" type="number" :max="currentProduct.BookInv" class="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" @input="validateDischargeQuantity" />    
                        </div>
                    </div>
                </template>
                <template #footer>
                    <DangerButton @click="showDischargeModal = false">{{ $t("actions.cancel") }}</DangerButton>
                    <PrimaryButton
                        class="ms-3 bg-green-500 hover:bg-green-600"
                        @click="scaricoManualeMultiplo ? addItem() : confirmDischarge()"
                        :disabled="!dischargeQuantity || dischargeQuantity <= 0 || (!scaricoManualeMultiplo && !selectedReason)"
                    >{{ $t("actions.confirm") }}</PrimaryButton>
                </template>
            </ConfirmationModal>

            <div v-if="scaricoManualeMultiplo && pendingItems.length > 0" class="mt-6">
                <div class="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-4">
                    <h3 class="text-lg font-semibold">{{ $t('sc.pending_items_title') }} ({{ pendingItems.length }})</h3>
                    <div class="flex flex-col md:flex-row gap-2 w-full md:w-auto">
                        <DangerButton class="w-full md:w-auto justify-center" @click="pendingItems = []; pendingStorageReasonMapping = {}">
                            <i class="fa fa-undo mr-2"></i> {{ $t('actions.reset') }}
                        </DangerButton>
                        <PrimaryButton
                            class="w-full md:w-auto justify-center bg-green-600 hover:bg-green-700"
                            @click="dischargePendingItems"
                            :disabled="!allPendingReasonsAssigned"
                        >
                            <i class="fa fa-check-double mr-2"></i> {{ $t('sc.discharge_all') }}
                        </PrimaryButton>
                    </div>
                </div>

                <div v-if="distinctPendingStorages.length > 0" class="mb-4 p-4 border rounded-lg bg-gray-50">
                    <h4 class="text-sm font-bold mb-3">{{ $t('sc.reasons_per_storage') }}</h4>
                    <table class="w-full table-fixed text-sm">
                        <thead>
                            <tr class="text-left border-b">
                                <th class="w-1/4 pb-2 pr-4">{{ $t('sc.table_storage') }}</th>
                                <th class="pb-2 pl-4">{{ $t('sc.table_reason') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="storage in distinctPendingStorages" :key="storage" class="border-b last:border-0">
                                <td class="py-2 pr-4 font-medium align-top">{{ storage }}</td>
                                <td class="py-2 pl-4 align-top">
                                    <v-select
                                        v-model="pendingStorageReasonMapping[storage]"
                                        :options="[{ Reason: '', Description: '--Nessuna Causale--' }, ...filteredReasonsForStorage(storage)]"
                                        :reduce="reason => reason.Reason"
                                        label="Reason"
                                        :clearable="false"
                                        :searchable="false"
                                        :placeholder="$t('sc.select_reason_placeholder')"
                                        class="reason-select w-full rounded-md focus:ring-2 focus:ring-blue-500"
                                        :class="{ 'border-red-500 ring-1 ring-red-500': !pendingStorageReasonMapping[storage], 'border-gray-300': pendingStorageReasonMapping[storage] }"
                                    >
                                        <template #option="{ Reason, Description }">
                                            <div class="whitespace-normal break-words"><template v-if="Reason">{{ Reason }} - </template>{{ Description }}</div>
                                        </template>
                                        <template #selected-option="{ Reason, Description }">
                                            <div class="whitespace-normal break-words"><template v-if="Reason">{{ Reason }} - </template>{{ Description }}</div>
                                        </template>
                                    </v-select>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="md:hidden space-y-3 mb-2">
                    <div
                        v-for="(item, idx) in pendingItems"
                        :key="idx"
                        :class="[
                            'border rounded-lg bg-white p-4 shadow-sm',
                            duplicateItemKeys[`${item.Item}|${item.Storage}|${item.Lot || ''}`] ? 'bg-yellow-100 border-yellow-400' : 'border-gray-200'
                        ]"
                    >
                        <div class="flex items-start justify-between gap-3">
                            <div class="min-w-0">
                                <div class="font-semibold text-gray-900 break-words">{{ item.Item }}</div>
                                <div class="mt-1 text-sm text-gray-600 break-words">{{ item.Description }}</div>
                            </div>
                            <button
                                @click="pendingItems.splice(idx, 1)"
                                class="flex-shrink-0 w-10 h-10 -mr-2 -mt-2 text-red-500 hover:text-red-700"
                            >
                                <i class="fa fa-times"></i>
                            </button>
                        </div>
                        <dl class="grid grid-cols-2 gap-x-4 gap-y-3 mt-4 text-sm">
                            <div>
                                <dt class="text-xs font-medium text-gray-500 uppercase">{{ $t('sc.table_storage') }}</dt>
                                <dd class="mt-1 text-gray-900 break-words">{{ item.Storage }}</dd>
                            </div>
                            <div>
                                <dt class="text-xs font-medium text-gray-500 uppercase">{{ $t('sc.header_lot') }}</dt>
                                <dd class="mt-1 text-gray-900 font-mono break-words">{{ item.Lot || '-' }}</dd>
                            </div>
                            <div>
                                <dt class="text-xs font-medium text-gray-500 uppercase">{{ $t('sc.header_quantity') }}</dt>
                                <dd class="mt-1 text-gray-900 font-mono">{{ item.Quantity }} {{ item.UoM }}</dd>
                            </div>
                        </dl>
                    </div>
                </div>

                <div class="hidden md:block overflow-x-auto border rounded-lg mb-2">
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50">
                            <tr>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('sc.header_item') }}</th>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('sc.header_description') }}</th>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('sc.table_storage') }}</th>
                                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('sc.header_lot') }}</th>
                                <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('sc.header_quantity') }}</th>
                                <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('sc.header_uom') }}</th>
                                <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16"></th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-200 bg-white">
                            <tr v-for="(item, idx) in pendingItems" :key="idx" :class="['hover:bg-gray-50', duplicateItemKeys[`${item.Item}|${item.Storage}|${item.Lot || ''}`]  ? 'bg-yellow-100 border-l-4 border-yellow-400' : '']" >
                                <td class="px-4 py-3 text-sm font-medium text-gray-900">{{ item.Item }}</td>
                                <td class="px-4 py-3 text-sm text-gray-500">{{ item.Description }}</td>
                                <td class="px-4 py-3 text-sm text-gray-700">{{ item.Storage }}</td>
                                <td class="px-4 py-3 text-sm text-gray-700 font-mono">{{ item.Lot || '-' }}</td>
                                <td class="px-4 py-3 text-sm text-gray-900 text-right font-mono">{{ item.Quantity }}</td>
                                <td class="px-4 py-3 text-sm text-gray-500 text-right">{{ item.UoM }}</td>
                                <td class="px-4 py-3 text-center">
                                    <button @click="pendingItems.splice(idx, 1)" class="text-red-500 hover:text-red-700">
                                        <i class="fa fa-times"></i>
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div v-if="csvProcessedRows.length > 0" class="mt-8 mb-10">
                <div class="flex items-center justify-between mb-4">
                    <h3 class="text-lg font-bold">{{ $t("sc.csv_data") }} ({{ csvProcessedRows.length }})</h3>
                    <div class="flex gap-2">
                        <button
                            class="text-white bg-red-500 hover:bg-red-600 font-medium rounded-lg text-sm px-4 py-2"
                            @click="clearCsvData"
                        >
                            {{ $t("actions.clear") }}
                        </button>
                        <PrimaryButton
                            class="bg-green-600 hover:bg-green-700"
                            @click="confirmBulkDischarge"
                            :disabled="isProcessingBulk || !allStorageReasonsAssigned"
                        >
                            <i class="fa fa-check-double mr-2"></i> {{ $t("actions.confirm_selected") }}
                        </PrimaryButton>
                    </div>
                </div>

                <!-- Tabella Associazione Depositi/Causali -->
                <div v-if="distinctCsvStorages.length > 0" class="mt-6 mb-6 p-4 border rounded-lg bg-gray-50">
                    <h3 class="text-md font-bold mb-3">{{ $t("sc.storage_reason_mapping") }}</h3>
                    <table class="w-full table-fixed text-sm">
                        <thead>
                            <tr class="text-left border-b">
                                <th class="w-1/4 pb-2 pr-4">{{ $t("gm.storage") }}</th>
                                <th class="pb-2 pl-4">{{ $t("sc.movement_reason") }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="storage in distinctCsvStorages" :key="storage" class="border-b last:border-0">
                                <td class="py-2 pr-4 font-medium align-top">{{ storage }}</td>
                                <td class="py-2 pl-4 align-top">
                                    <v-select
                                        v-model="storageReasonMapping[storage]"
                                        :options="[{ Reason: '', Description: '--Nessuna Causale--' }, ...filteredReasonsForStorage(storage)]"
                                        :reduce="reason => reason.Reason"
                                        label="Reason"
                                        :clearable="false"
                                        :searchable="false"
                                        :placeholder="$t('sc.select_reason')"
                                        class="reason-select w-full rounded-md focus:ring-2 focus:ring-blue-500"
                                        :class="{ 'border-red-500 ring-1 ring-red-500': !storageReasonMapping[storage], 'border-gray-300': storageReasonMapping[storage] }"
                                    >
                                        <template #option="{ Reason, Description }">
                                            <div class="whitespace-normal break-words"><template v-if="Reason">{{ Reason }} - </template>{{ Description }}</div>
                                        </template>
                                        <template #selected-option="{ Reason, Description }">
                                            <div class="whitespace-normal break-words"><template v-if="Reason">{{ Reason }} - </template>{{ Description }}</div>
                                        </template>
                                    </v-select>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <PageNormalTable
                    :headers="headersCsv"
                    :items="csvProcessedRows"
                    :row-props="getRowProps"
                    density="compact"
                    hide-default-footer
                >
                    <template #header.selected>
                        <input 
                            type="checkbox" 
                            :checked="isAllSelected" 
                            @change="toggleSelectAll"
                            class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                    </template>
                    <template #item.csv_item="{ item }">
                        <div class="flex flex-col">
                            <span class="font-bold text-blue-900">{{ item.csv_item }}</span>
                            <span v-if="item.db_item && item.db_item !== item.csv_item" class="text-xs text-orange-600">DB: {{ item.db_item }}</span>
                        </div>
                    </template>
                    <template #item.lotto_comparison="{ item }">
                        <div class="flex items-center gap-1 text-xs">
                            <span :class="{'text-red-600 font-bold': item.csv_lot && item.csv_lot !== item.Lot}">{{ item.csv_lot || '-' }}</span>
                            <span>/</span>
                            <span class="text-blue-800">{{ item.Lot || '-' }}</span>
                        </div>
                    </template>
                    <template #item.storage_comparison="{ item }">
                        <div class="flex items-center gap-1 text-xs">
                            <span :class="{'text-red-600 font-bold': item.csv_storage !== item.Storage}">{{ item.csv_storage }}</span>
                            <span>/</span>
                            <span class="text-blue-800">{{ item.Storage || '-' }}</span>
                        </div>
                    </template>
                    <template #item.BookInv="{ item }">
                        <span :class="{'text-red-600 font-bold': item.db_item && Number(item.BookInv) < Number(item.csv_quantity)}">
                            {{ item.db_item ? Number(item.BookInv || 0) : '-' }}
                        </span>
                    </template>
                    <template #item.csv_quantity="{ item }">
                        <span class="font-bold">{{ item.csv_quantity }}</span>
                    </template>
                    <template #item.status="{ item }">
                        <div class="text-xs font-bold uppercase">
                            <span v-if="!item.db_item" class="text-red-600">{{ $t('sc.item_not_found') }}</span>
                            <div v-else class="flex flex-col gap-0.5">
                                <span v-if="item.csv_storage !== item.Storage" class="text-orange-600">{{ $t('sc.storage_mismatch') }}</span>
                                <span v-if="item.csv_lot && item.csv_lot !== item.Lot" class="text-orange-600">{{ $t('sc.lot_mismatch') }}</span>
                                <span v-if="Number(item.BookInv) < Number(item.csv_quantity)" class="text-red-600">{{ $t('sc.insufficient_stock') }}</span>
                                <span v-if="item.db_item && item.csv_storage === item.Storage && (!item.csv_lot || item.csv_lot === item.Lot) && Number(item.BookInv) >= Number(item.csv_quantity)" class="text-green-600">OK</span>
                            </div>
                        </div>
                    </template>
                    <template #item.csv_status="{ item }">
                        <div class="text-xs font-bold">
                            <span v-if="item.csv_status === 'SENT' || item.csv_status === 'PENDING'" class="text-blue-600">
                                {{ $t('sc.to_discharge') }}
                            </span>
                            <span v-else-if="item.csv_status === 'DISCHARGED'" class="text-green-600">
                                {{ $t('sc.discharged') }}
                            </span>
                            <span v-else>{{ item.csv_status }}</span>
                        </div>
                    </template>
                    <!-- Nuovo slot per il checkbox -->
                    <template #item.selected="{ item }">
                        <input 
                            type="checkbox" 
                            v-model="item.selected" 
                            :disabled="!isItemOk(item)"
                            class="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                    </template>
                </PageNormalTable>
            </div>

            <ToastNotification :show="toast.show" :message="toast.message" :type="toast.type" @update:show="toast.show = $event" />
        </div>
    </div>
</template>

<script>
import BreadCrumbs from "@/Components/BreadCrumbs.vue";
import ConfirmationModal from "@/Components/ConfirmationModal.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import DangerButton from "@/Components/DangerButton.vue";
import ToastNotification from "@/Components/ToastNotification.vue";
import Loader from "@/Components/Loader.vue";
import PageNormalTable from "@/Components/PageNormalTable.vue";
import { useLoader } from "@/Composables/useLoader.js";
import axios from "axios";

export default {
    name: "ScaricoComponenti",

    setup() {
        const { isLoading, loadingText, showLoader, hideLoader } = useLoader();
        return { isLoading, loadingText, showLoader, hideLoader };
    },

    components: {
        BreadCrumbs,
        ConfirmationModal,
        PrimaryButton,
        DangerButton,
        ToastNotification,
        Loader,
        PageNormalTable,
    },

    props: {
        storages: { type: Array, default: () => [] },
        inventoryReasons: { type: Array, default: () => [] },
        defaultStorage: { type: [Object, Boolean], default: false },
        csvImportEnabled: { type: Boolean, default: false },
        reasonMapping: { type: [Object, Array], default: () => ({}) },
        scaricoManualeMultiplo: { type: Boolean, default: false },
        modalitaScaricoComponenti: { type: [Number, String, Object], default: 0 },
    },

    data() {
        return {
            selectedStorage: "",
            selectedReason: null,
            barcodeSearch: "",
            searchMode: "barcode",
            showDischargeModal: false,
            showDuplicateModal: false,
            showProductSelectionModal: false,
            productOptions: [],
            currentProduct: null,
            availableLots: [],
            selectedLot: "",
            dischargeQuantity: 1,
            lastSearchItems: [],
            lotItemsByLot: {},
            groupedItemsByProduct: {},
            csvProcessedRows: [],
            currentFileName: "",
            isProcessingBulk: false,
            toast: { show: false, message: "", type: "success" },
            storageReasonMapping: {},
            pendingItems: [],
            pendingStorageReasonMapping: {},
        };
    },

    computed: {
        distinctCsvStorages() {
            const storages = this.csvProcessedRows.map(row => row.csv_storage);
            this.initStorageReasonMapping();
            return [...new Set(storages)].filter(Boolean);
        },
        isAllSelected() {
            return this.csvProcessedRows.length > 0 && this.csvProcessedRows.every(r => r.selected);
        },  
        isDefaultStorageConfigured() {
            return this.defaultStorage && typeof this.defaultStorage === "object";
        },
        hasLotSelection() {
            return this.availableLots.length > 1;
        },
        navigation() {
            return [
                { name: "Dashboard", href: route("dashboard") },
                {
                    name: this.$t("modules.gestione-magazzino"),
                    href: route("modules.home", "gestione-magazzino"),
                },
                {
                    name: this.$t("modules.scarico-componenti"),
                    href: "#", 
                },
            ];
        },
        headersCsv() {
            return [
                { title: this.$t("sc.csv_header_item"), key: "csv_item", sortable: false },
                { title: this.$t("sc.csv_header_lot_comparison"), key: "lotto_comparison", sortable: false },
                { title: this.$t("sc.csv_header_storage_comparison"), key: "storage_comparison", sortable: false },
                { title: this.$t("sc.csv_header_book_inv"), key: "BookInv", sortable: false, align: 'end' },
                { title: this.$t("sc.csv_header_quantity"), key: "csv_quantity", sortable: false, align: 'end' },
                { title: "", key: "status", sortable: false, align: 'center' },
                { title: this.$t("sc.csv_header_status"), key: "csv_status", sortable: false, align: 'center' },
                { title: this.$t("sc.to_discharge"), key: "selected", sortable: false, align: 'center' },
            ];
        },
        filteredReasons() {
            const mapping = this.reasonMapping || {};
            const storage = this.selectedStorage;
            if (!storage || !mapping[storage]) {
                if (mapping['default']) {
                    const val = mapping['default'];
                    const allowed = Array.isArray(val) ? val : [val];
                    return this.inventoryReasons.filter(r => allowed.includes(r.Reason));
                }
                return this.inventoryReasons;
            }
            const val = mapping[storage];
            const allowed = Array.isArray(val) ? val : [val];
            return this.inventoryReasons.filter(r => allowed.includes(r.Reason));
        },
        allStorageReasonsAssigned() {
            return this.distinctCsvStorages.every(s => !!this.storageReasonMapping[s]);
        },
        distinctPendingStorages() {
            return [...new Set(this.pendingItems.map(i => i.Storage))].filter(Boolean);
        },
        allPendingReasonsAssigned() {
            return this.distinctPendingStorages.length > 0 &&
                this.distinctPendingStorages.every(s => !!this.pendingStorageReasonMapping[s]);
        },
        duplicateItemKeys() {
            const seen = {};
            const duplicates = {};
            this.pendingItems.forEach(item => {
                const key = `${item.Item}|${item.Storage}|${item.Lot || ''}`;
                if (seen[key]) {
                    duplicates[key] = true;
                }
                seen[key] = true;
            });
            return duplicates;
        },

    },

    mounted() {
        if (this.isDefaultStorageConfigured) {
            this.selectedStorage = this.defaultStorage.Storage;
            this.checkIfSingleReason();
        }
    },

    methods: {
        triggerImport() {
            this.$refs.csvInput.click();
        },
        toggleSelectAll(e) {
            const checked = e.target.checked;
            this.csvProcessedRows.forEach(r => {
                if (this.isItemOk(r)) {
                    r.selected = checked;
                } else {
                    r.selected = false;
                }
            });
        },

        handleCsvFile(event) {
            const file = event.target.files[0];
            if (!file) return;

            this.currentFileName = file.name;

            const reader = new FileReader();
            reader.onload = (e) => {
                const text = e.target.result;
                this.parseCsv(text);
            };
            reader.readAsText(file);
            event.target.value = '';
        },

        async parseCsv(text) {
            const lines = text.split(/\r?\n/);
            if (lines.length === 0) return;

            // 1. Validazione Header Puntuale
            const expectedColumns = ["createdat", "operatorid", "deposito", "articolo", "lotto", "quantita", "stato"];
            const actualColumns = lines[0].split(/[;,]/).map(col => col.trim().toLowerCase());

            // Controllo 1: Numero esatto di colonne
            const isLengthCorrect = actualColumns.length === expectedColumns.length;
            
            // Controllo 2: Contenuto e ordine esatto delle colonne
            const isContentCorrect = expectedColumns.every((val, index) => val === actualColumns[index]);

            if (!isLengthCorrect || !isContentCorrect) {
                const expectedHeaderStr = "createdAt;operatorId;deposito;articolo;lotto;quantita;stato";
                this.showToast(
                    this.$t("sc.invalid_csv_format", { count: expectedColumns.length, header: expectedHeaderStr }), 
                    "error"
                );
                return;
            }

            const rawData = [];
            lines.forEach((line, index) => {
                if (!line.trim() || index === 0) return;
                const columns = line.split(/[;,]/);
                
                if (columns[3] || columns[4]) {
                    rawData.push({
                        item: columns[3]?.trim() || '',
                        lot: columns[4]?.trim() || '',
                        storage: columns[2]?.trim() || this.selectedStorage, // Usa deposito da CSV, fallback su quello selezionato
                        quantity: parseFloat(columns[5]?.replace(',', '.') || 0),
                        csv_status: columns[6]?.trim() || '',
                        external_id: `${this.currentFileName.replace('.csv', '')}_${index}`,
                        operator_id: columns[1]?.trim() || '',
                    });
                }
            });

            if (rawData.length === 0) {
                this.showToast(this.$t("sc.empty_or_invalid_csv"), "error");
                return;
            }
            
            

            this.showLoader(this.$t("sc.processing_csv"));
            
            try {
                // CHIAMATA BATCH: Invia tutto il set di dati
                const res = await axios.post(route("modules.gm.searchitems_batch"), {
                    rows: rawData
                });
                
                
                this.csvProcessedRows = res.data.processed.map((row, idx) => ({
                    ...row,
                    selected: !!(row.db_item && 
                            row.csv_storage === row.Storage && 
                            (!row.csv_lot || row.csv_lot === row.Lot) && 
                            Number(row.BookInv) >= Number(row.csv_quantity) &&
                            row.csv_status !== "DISCHARGED")
                }));

                // Pre-popola le associazioni depositi se ci sono nuovi depositi
                this.distinctCsvStorages.forEach(storage => {
                    if (!this.storageReasonMapping[storage]) {
                        this.storageReasonMapping[storage] = "";
                    }
                });

            } catch (e) {
                console.error("Batch search error", e);
                this.showToast(this.$t("sc.batch_processing_error"), "error");
            } finally {
                this.hideLoader();
            }
        },

        getRowProps(item, index) {
            // Vuetify 3 row-props can pass item directly or as an object depending on version/config
            // We check both cases to be safe
            const row = item?.item || item;
            
            if (!row.db_item) return { class: '!bg-orange-200' }; // Non trovato
            
            const stock = Number(row.BookInv || 0);
            const req = Number(row.csv_quantity || 0);
            
            if (stock < req) {
                return { class: '!bg-red-200' };
            } else {
                return { class: '!bg-green-200' };
            }
        },

        clearCsvData() {
            this.csvProcessedRows = [];
        },

        async confirmBulkDischarge() {
            if (!this.csvProcessedRows.length) return;
            
            const toProcess = this.csvProcessedRows.filter(r => 
                r.selected &&
                r.db_item && 
                r.csv_storage === r.Storage && 
                (!r.csv_lot || r.csv_lot === r.Lot) && 
                Number(r.BookInv) >= Number(r.csv_quantity)
            );

            if (toProcess.length === 0) {
                this.showToast(this.$t("sc.no_items_to_process"), "warning");
                return;
            }

            // Group by storage
            const groups = toProcess.reduce((acc, row) => {
                const s = `${row.csv_storage}||${row.specificator_type || 0}||${row.specificator || ''}`;
                if (!acc[s]) acc[s] = [];
                acc[s].push(row);
                return acc;
            }, {});

            this.isProcessingBulk = true;
            this.showLoader(this.$t("sc.loading_discharge"));
            
            let successGroups = 0;
            let totalGroups = Object.keys(groups).length;
            let errors = [];

            for (const storage in groups) {
                const realStorage = storage.split('||')[0];
                const reason = this.storageReasonMapping[realStorage];
                if (!reason) {
                    errors.push(this.$t("sc.missing_reason_for_storage", { storage: realStorage }));
                    continue;
                }

                const items = groups[storage].map(row => ({
                    item: row.db_item,
                    quantity: row.csv_quantity,
                    lot: row.csv_lot || row.Lot || "",
                    uom: row.UoM,
                    specificator: row.specificator,
                    specificator_type: row.specificator_type,
                    external_id: row.external_id || null,
                    operator_id: row.operator_id || '',
                }));

                try {
                    const res = await axios.post(route("modules.gm.scarico"), {
                        storage: realStorage,
                        reason: reason,
                        items: items,

                    });

                    if (res.data.success) {
                        successGroups++;
                        // Aggiorna lo stato delle righe nel CSV
                        groups[storage].forEach(row => {
                            row.csv_status = 'DISCHARGED';
                            row.selected = false;
                        });
                    } else {
                        errors.push(`${storage}: ${res.data.message}`);
                    }
                } catch (e) {
                    errors.push(`${storage}: ${this.$t('sc.error_generic')}`);
                }
            }

            this.hideLoader();
            this.isProcessingBulk = false;

            if (errors.length === 0) {
                this.showToast(this.$t("sc.all_items_discharged"), "success");
            } else {
                const errorMsg = errors.join(" | ");
                this.showToast(this.$t("sc.discharge_with_errors_summary", { success: successGroups, total: totalGroups }) + ` ${errorMsg}`, "warning");
                // Optional: keep processed rows that failed? For now we just notify.
            }
        },

        searchSelectedItem() {
            if (this.searchMode === "lot") {
                this.searchItemFromLot();
                return;
            }

            this.searchItemfromBarcode();
        },

        searchItemfromBarcode() {
            this.executeItemSearch("barcode");
        },

        searchItemFromLot() {
            this.executeItemSearch("lot");
        },

        async executeItemSearch(searchType) {
            if (!this.barcodeSearch.trim() || !this.selectedStorage) {
                this.showToast(this.$t("sc.enter_barcode_and_select_storage"), "warning");
                return;
            }

            this.showLoader(this.$t("ui.loader.default_text"));
            try {
                const response = await axios.post(route("modules.gm.searchitem"), {
                    barcode: this.barcodeSearch.trim(),
                    storage: this.selectedStorage,
                    search_type: searchType,
                })
                .then((response) => {
                    const data = response.data || {};
                    const items = Array.isArray(data.items) ? data.items : [];
                    

                    if (!items.length) {
                        this.showToast(this.$t("sc.item_not_found"), "warning");
                        return;
                    }
                    
                    if (items.length > 1) {
                        this.productOptions = items;
                        this.showProductSelectionModal = true;
                    } else {
                        this.selectProductForDischarge(items[0]);
                    }
                })
                
            } catch {
                this.showToast(this.$t("sc.search_error"), "error");
            } finally {
                this.hideLoader();
            }
        },


        addItem(duplicate = false) {
            
            if (!duplicate) {
                if (this.pendingItems.some(i => i.Item === this.currentProduct.Item && i.Storage === this.currentProduct.Storage && i.Lot === (this.selectedLot || String(this.currentProduct.Lot || '')))) {
                    this.showDischargeModal = false;
                    this.showDuplicateModal = true;
                    return; 
                }
            }

            const storage = this.currentProduct.Storage;
            this.pendingItems.push({
                Item: this.currentProduct.Item,
                Description: this.currentProduct.Description,
                Storage: storage,
                Lot: this.selectedLot || String(this.currentProduct.Lot || ''),
                Quantity: this.dischargeQuantity,
                UoM: this.currentProduct.UoM,
                BookInv: this.currentProduct.BookInv,
                specificator: this.currentProduct.Specificator,
                specificator_type: this.currentProduct.SpecificatorType,
            });
            if (!this.pendingStorageReasonMapping[storage]) {
                const mapping = this.reasonMapping || {};
                const val = mapping[storage] || mapping['default'] || '';
                this.pendingStorageReasonMapping[storage] = Array.isArray(val) ? val[0] : val;
            }
            this.showDischargeModal = false;
            this.barcodeSearch = '';
        },  

        handleConfirmDuplicate() {
            this.showDuplicateModal = false;
            this.addItem(true);
        },

        isMaxQuantityMode() {
            const mode = this.modalitaScaricoComponenti;
            const modeId = mode && typeof mode === "object" ? mode.id : mode;
            return Number(modeId) === 1;
        },

        selectProductForDischarge(product) {
            this.showProductSelectionModal = false;
            if (!product.BookInv || product.BookInv <= 0) {
                this.showToast(this.$t("sc.product_not_available"), "error");
                return;
            }
            this.currentProduct = {
                ...product,
                BookInv: Number(product.BookInv),
                MinimumStock: Number(product.MinimumStock || 0),
            };

            this.dischargeQuantity = this.isMaxQuantityMode() ? this.currentProduct.BookInv : 1;
            this.availableLots = Object.keys(this.lotItemsByLot);
            this.selectedLot = this.availableLots.length ? this.availableLots[0] : String(product.Lot || "");
            this.showDischargeModal = true;
            this.$nextTick(() => {
                if (this.$refs.quantityInput) {
                    this.$refs.quantityInput.focus();
                    this.$refs.quantityInput.select();
                }
            });
        },

        validateDischargeQuantity() {
            if (this.dischargeQuantity > this.currentProduct.BookInv) {
                this.dischargeQuantity = this.currentProduct.BookInv;
                this.showToast(this.$t("sc.quantity_reduced_to_max"), "warning");
            } 
        },

        async dischargePendingItems() {
            if (!this.pendingItems.length) return;

            const groups = this.pendingItems.reduce((acc, row) => {
                if (!acc[row.Storage]) acc[row.Storage] = [];
                acc[row.Storage].push(row);
                return acc;
            }, {});

            this.showLoader(this.$t('sc.loading_discharge'));

            const results = await Promise.all(
                Object.entries(groups).map(async ([storage, rows]) => {
                    const reason = this.pendingStorageReasonMapping[storage];
                    if (!reason) {
                        return { storage, success: false, error: this.$t('sc.missing_reason_for_storage', { storage }) };
                    }
                    const items = rows.map(row => ({
                        item: row.Item,
                        quantity: row.Quantity,
                        lot: row.Lot || '',
                        uom: row.UoM,
                        specificator: row.specificator,
                        specificator_type: row.specificator_type,
                        operator_id: '',
                        external_id: null,
                    }));
                    try {
                        const res = await axios.post(route('modules.gm.scarico'), { storage, reason, items });
                        if (res.data.success) {
                            return { storage, success: true };
                        } else {
                            return { storage, success: false, error: `${storage}: ${res.data.message || this.$t('sc.error_generic')}` };
                        }
                    } catch {
                        return { storage, success: false, error: `${storage}: ${this.$t('sc.error_generic')}` };
                    }
                })
            );

            this.hideLoader();

            const successStorages = results.filter(r => r.success).map(r => r.storage);
            const errors = results.filter(r => !r.success).map(r => r.error);

            if (successStorages.length) {
                this.pendingItems = this.pendingItems.filter(i => !successStorages.includes(i.Storage));
                successStorages.forEach(s => delete this.pendingStorageReasonMapping[s]);
            }

            if (errors.length === 0) {
                this.showToast(this.$t('sc.all_items_discharged'), 'success');
            } else {
                this.showToast(errors.join(' | '), 'warning');
            }
        },

        async confirmDischarge() {
            if (!this.currentProduct || !this.dischargeQuantity || !this.selectedReason) return;
            this.showDischargeModal = false;
            this.showLoader(this.$t('sc.loading_discharge'));
            try {
                const res = await axios.post(route('modules.gm.scarico'), {
                    storage: this.currentProduct.Storage,
                    reason: this.selectedReason,
                    items: [{
                        item: this.currentProduct.Item,
                        quantity: this.dischargeQuantity,
                        lot: this.selectedLot || String(this.currentProduct.Lot || ''),
                        uom: this.currentProduct.UoM,
                        specificator: this.currentProduct.Specificator || '',
                        specificator_type: this.currentProduct.SpecificatorType || 0,
                        operator_id: '',
                        external_id: null,
                    }],
                });
                if (res.data.success) {
                    this.showToast(this.$t('sc.discharge_completed'), 'success');
                } else {
                    this.showToast(res.data.message || this.$t('sc.error_generic'), 'error');
                }
            } catch {
                this.showToast(this.$t('sc.discharge_during_error'), 'error');
            } finally {
                this.hideLoader();
                this.barcodeSearch = '';
            }
        },

        showToast(message, type = "success") {
            this.toast.message = message;
            this.toast.type = type;
            this.toast.show = true;
        },

        isItemOk(item) {
            return !!(item.db_item && 
                item.csv_storage === item.Storage && 
                (!item.csv_lot || item.csv_lot === item.Lot) && 
                Number(item.BookInv) >= Number(item.csv_quantity) &&
                item.csv_status !== 'DISCHARGED');
        },
        initStorageReasonMapping() {
            const mapping = this.reasonMapping || {};
            const storages = [...new Set(this.csvProcessedRows.map(row => row.csv_storage))].filter(Boolean);
            storages.forEach(storage => {

                if (this.storageReasonMapping[storage]) return;
                if (mapping[storage]) {
                    const val = mapping[storage];
                    this.storageReasonMapping[storage] = Array.isArray(val) ? val[0] : val;
                } else if (mapping['default']) {
                    const val = mapping['default'];
                    this.storageReasonMapping[storage] = Array.isArray(val) ? val[0] : val;
                }
            });
        },
        filteredReasonsForStorage(storage) {
            const mapping = this.reasonMapping || {};
            if (mapping[storage]) {
                const val = mapping[storage];
                const allowed = Array.isArray(val) ? val : [val];
                return this.inventoryReasons.filter(r => allowed.includes(r.Reason));
            }
            if (mapping['default']) {
                const val = mapping['default'];
                const allowed = Array.isArray(val) ? val : [val];
                return this.inventoryReasons.filter(r => allowed.includes(r.Reason));
            }
            return this.inventoryReasons;
        },
    },

    watch: {
        selectedStorage() {
            if (this.filteredReasons.length === 1 && !this.scaricoManualeMultiplo) {
                this.selectedReason = this.filteredReasons[0].Reason;
            }
            else this.selectedReason = null;
        },
        showDischargeModal(val) {
            if (val) this.$nextTick(() => { if (this.$refs.quantityInput) { this.$refs.quantityInput.focus(); this.$refs.quantityInput.select(); } });
        },
        selectedLot(newLot) {
            if (!newLot || !this.lotItemsByLot) return;
            const item = this.lotItemsByLot[newLot];
            if (item) {
                this.currentProduct = { ...item, BookInv: Number(item.BookInv), MinimumStock: Number(item.MinimumStock || 0) };
                if (this.isMaxQuantityMode()) {
                    this.dischargeQuantity = this.currentProduct.BookInv;
                } else {
                    this.validateDischargeQuantity();
                }
            }
        },
    },
};
</script>

<style scoped>
:deep(.reason-select .vs__dropdown-toggle) {
    min-height: 1.25rem;
    height: auto;
    padding: 0.25rem 0.5rem;
    align-items: center;
    border: 0;
}

:deep(.reason-select .vs__selected-options) {
    min-width: 0;
    flex-wrap: nowrap;
}

:deep(.reason-select .vs__selected) {
    flex: 1 1 auto;
    min-width: 0;
    max-width: 100%;
    margin: 0;
    white-space: normal;
    overflow-wrap: anywhere;
    line-height: 1.25;
}

:deep(.reason-select .vs__search) {
    display: none;
}

:deep(.v-data-table__tr) {
    transition: background-color 0.2s ease;
}

:deep(.v-data-table__tr td) {
    background-color: inherit !important;
}
:deep(.v-data-table__tr td:nth-child(5)),
:deep(.v-data-table__th:nth-child(5)) {
    border-left: 2px solid rgba(0,0,0,0.1) !important;
}
</style>
